import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Session du back-office.
 *
 * Elle était restaurée depuis localStorage sans aucune vérification : ni la
 * date d'expiration inscrite dans le JWT, ni l'avis du serveur. Le jeton vit
 * 24 h ; passé ce délai, l'application gardait toute son allure d'admin
 * connecté — barre latérale, rôle affiché, écrans ouverts — mais se dégradait
 * en silence, route par route. Les véhicules s'affichaient encore (le
 * catalogue répond sans jeton, en version expurgée), la liste des comptes ne
 * se chargeait plus, et un champ de recherche finissait par répondre « aucun
 * compte ne correspond » à un email qui existait. Rien ne disait « votre
 * session a expiré ».
 *
 * Trois garde-fous, du moins coûteux au plus sûr :
 *
 *   1. l'expiration du JWT est lue localement — un jeton périmé n'est pas
 *      restauré, et un jeton qui périme pendant que l'onglet est ouvert
 *      déconnecte à l'heure dite ;
 *   2. le serveur confirme le jeton au chargement — un jeton valide par la date
 *      peut être refusé (session révoquée, compte supprimé, secret changé), et
 *      la réponse rafraîchit le rôle, qu'une rétrogradation ne soit pas
 *      invisible jusqu'à la prochaine connexion ;
 *   3. tout 401 renvoyé par l'API en cours d'usage déconnecte — c'est le filet
 *      pour ce que les deux premiers ne voient pas.
 */

interface User {
  id?: number | string;
  id_user?: string;
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (token: string, user: User) => void;
  logout: (reason?: string) => void;
  loading: boolean;
  /** Pourquoi la dernière déconnexion a eu lieu, pour l'afficher sur l'écran de connexion. */
  logoutReason: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_API_URL || '';
const SESSION_EXPIRED_MESSAGE = 'Votre session a expiré. Reconnectez-vous.';

/**
 * Date d'expiration (ms) inscrite dans un JWT, ou `null` si illisible.
 *
 * Lecture locale du seul champ `exp`, sans vérifier la signature — ce n'est
 * pas le rôle du client, c'est celui du serveur. On veut juste savoir s'il est
 * encore utile d'essayer.
 */
export function readTokenExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')));
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

const userId = (user: User | null) => (user?.id_user || user?.id || '').toString();

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [logoutReason, setLogoutReason] = useState<string | null>(null);
  const navigate = useNavigate();

  // Le jeton courant, lisible depuis des rappels installés une seule fois
  // (intercepteur fetch, minuteur d'expiration) sans qu'ils se ferment sur une
  // valeur périmée.
  const tokenRef = useRef<string | null>(null);
  tokenRef.current = token;

  const clearSession = useCallback((reason?: string) => {
    tokenRef.current = null;
    setToken(null);
    setUser(null);
    setLogoutReason(reason ?? null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  }, []);

  const logout = useCallback((reason?: string) => {
    clearSession(reason);
    navigate('/signin');
  }, [clearSession, navigate]);

  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  // --- 1. Restauration, avec vérification de la date puis du serveur --------
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');

    if (!storedToken || !storedUser) {
      setLoading(false);
      return;
    }

    let userData: User;
    try {
      userData = JSON.parse(storedUser);
    } catch (error) {
      console.error('Erreur lors du parsing des données utilisateur:', error);
      clearSession();
      setLoading(false);
      return;
    }

    const expiresAt = readTokenExpiry(storedToken);
    if (expiresAt !== null && expiresAt <= Date.now()) {
      clearSession(SESSION_EXPIRED_MESSAGE);
      setLoading(false);
      return;
    }

    let cancelled = false;

    // Le serveur a le dernier mot. `loading` reste levé jusqu'à sa réponse :
    // afficher l'ossature admin puis la retirer une seconde plus tard serait
    // pire que le squelette d'attente, qui existe précisément pour ça.
    (async () => {
      try {
        const response = await fetch(`${API_URL}api/users/${userId(userData)}`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });

        if (cancelled) return;

        if (response.status === 401) {
          clearSession(SESSION_EXPIRED_MESSAGE);
          return;
        }

        if (response.ok) {
          // Le rôle est relu du serveur, pas du stockage : c'est lui qui a été
          // rétrogradé ou promu entre-temps, et c'est sur lui que les gardes de
          // route décident.
          const fresh = await response.json().catch(() => null);
          const merged = fresh && typeof fresh === 'object' ? { ...userData, ...fresh } : userData;
          setToken(storedToken);
          setUser(merged);
          localStorage.setItem('user', JSON.stringify(merged));
          return;
        }

        // Toute autre réponse (403, 404, 500…) n'est pas un jugement sur le
        // jeton : on garde la session, le filet du 401 fera le reste.
        setToken(storedToken);
        setUser(userData);
      } catch (error) {
        // Réseau coupé, API indisponible : ce n'est pas une session expirée, et
        // déconnecter quelqu'un parce que le Wi-Fi a toussé serait injuste.
        if (cancelled) return;
        console.error('Vérification de session impossible, session conservée :', error);
        setToken(storedToken);
        setUser(userData);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [clearSession]);

  // --- 2. Déconnexion à l'heure d'expiration du jeton -----------------------
  useEffect(() => {
    if (!token) return;
    const expiresAt = readTokenExpiry(token);
    if (expiresAt === null) return;

    const check = () => {
      if (Date.now() >= expiresAt) logoutRef.current(SESSION_EXPIRED_MESSAGE);
    };

    // setTimeout plafonne à 2^31-1 ms (~24,8 jours) ; au-delà il se déclenche
    // immédiatement. Le jeton vit 24 h, mais mieux vaut ne pas dépendre d'une
    // valeur de configuration du serveur.
    const delay = Math.min(Math.max(expiresAt - Date.now(), 0), 2 ** 31 - 1);
    const timer = window.setTimeout(check, delay);

    // Un portable qui sort de veille : le minuteur a pu être retardé, et
    // l'utilisateur reprend l'onglet là où il l'avait laissé.
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, [token]);

  // --- 3. Tout 401 de l'API en cours d'usage déconnecte ---------------------
  // Une quinzaine de services appellent `fetch` chacun de leur côté, avec leur
  // propre traitement d'erreur — ou sans. Plutôt que d'en modifier quinze, un
  // seul point d'observation : la réponse est regardée, jamais altérée, et
  // seulement pour les requêtes vers notre API qui portaient notre jeton. Un
  // 401 reçu alors que nous n'avons pas de jeton (page de connexion, mot de
  // passe erroné) ne concerne pas la session.
  useEffect(() => {
    if (!API_URL) return;
    const originalFetch = window.fetch;

    window.fetch = async (input, init) => {
      const response = await originalFetch(input, init);
      const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
      if (response.status === 401 && tokenRef.current && url.startsWith(API_URL)) {
        logoutRef.current(SESSION_EXPIRED_MESSAGE);
      }
      return response;
    };

    return () => { window.fetch = originalFetch; };
  }, []);

  const login = (newToken: string, userData: User) => {
    setLogoutReason(null);
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('authToken', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const isAuthenticated = !!token && !!user;

  const value = {
    user,
    token,
    isAuthenticated,
    login,
    logout,
    loading,
    logoutReason
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
